import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styles from "./EagleTracker.module.css";

import QualifyHeader from "../../components/QualifyHeader";
import { SelectDropdown } from "../../components/SelectDropdown/SelectDropdown";
import { EagleList } from "../../components/EagleList/EagleList";
import SettingsModal from "../../components/SettingsModal";
import { SearchModal } from "../../components/SearchModal/SearchModal";
import { TagMonster } from "../../components/TagMonster/TagMonster";
import Search from "./imgs/search.svg";
import Qualifications from "./imgs/Qualifications.png";
import Close from "./imgs/Close_Button.svg";
import Loader from "components/Loader";

import { useEagleTracker } from "./useEagleTracker";
import { useTranslation } from "react-i18next";

const transformEagleList = (data, onClick) =>
  data.map((e) => ({
    ibo: e.ibo,
    name: e.iboName,
    sponsorName: e.sponsorName ?? e.uplineName ?? "Mock Sponsor",
    avatar: [e.iboAAvatar, e.iboBAvatar].filter(Boolean).map((img) => img),
    onClick: () => onClick(e.ibo),
    totalBonus: e.totalBonus,
    totalPremier: e.totalPremier,
    totalMajorFunctions: e.totalMajorFunctions,
  }));

// Backend stat field mapping lives here.
// Update aliases/fragments below when backend response names are finalized.
// TODO(backend-contract): Replace with confirmed field names from:
// GET /eagle/event/{event}/year/{year}
// Expected UI metrics contract (placeholder paths, not confirmed):
// - PV: userProfile.stats.pv
// - VCS: userProfile.stats.vcsPercent
// - Legs: userProfile.stats.qualifyingLegs
// - Qualification: userProfile.stats.qualificationStatus
// Finalization steps once backend keys are confirmed:
// 1) Put confirmed raw keys first in each aliases[] list below.
// 2) Remove broad fallback aliases/fragments that are no longer needed.
// 3) Remove any temporary mapping/debug scaffolding.
const STAT_KEY_MAP = {
  pv: {
    aliases: [
      "pv",
      "personalPv",
      "personalPV",
      "personalVolume",
      "personalUsePV",
      "myPV",
      "totalBonus",
      "bonusLevelPercentage",
    ],
    fragments: ["pv", "personalvolume", "bonus"],
  },
  vcs: {
    aliases: [
      "vcs",
      "vcsPercent",
      "vcsPercentage",
      "personalVcs",
      "personalVCS",
      "personalVCSPercent",
      "vcsPct",
      "totalPremier",
      "premierPlus",
    ],
    fragments: ["vcs", "premier"],
  },
  legs: {
    aliases: [
      "legs",
      "bonusLegs",
      "qualifyingLegs",
      "qualifiedLegs",
      "legsCount",
      "bonusLegCount",
      "totalMajorFunctions",
      "majorFunctions",
    ],
    fragments: ["leg", "majorfunction"],
  },
  qualification: {
    aliases: [
      "qualification",
      "qualificationPercent",
      "qualificationStatus",
      "qualified",
      "qualifies",
      "qualifiedEagle",
      "qualifiedDoubleEagle",
    ],
    fragments: ["qualif", "qualified"],
  },
};

export const EagleTracker = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const [qualOpened, setQualOpened] = useState(false);
  const [eagleData, setEagleData] = useState(null);
  const [activeTab, setActiveTab] = useState("My Parameters");
  const [filterSettings, setFilterSettings] = useState({
    bonusLegs: 3,
    pmLegs: 1,
    functions: 1,
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1900 + 1 },
    (_, i) => currentYear - i
  );
  const [year, setYear] = useState(currentYear);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const [selectedMonth, setSelectedMonth] = useState(
    months[new Date().getMonth()]
  );

  const functions = ["Family Reunion", "Summer Conference", "Next Qualifying"];
  const [selectedFunc, setSelectedFunc] = useState(functions[0]);

  const locations = ["Spokane, WA", "Tacoma, WA", "San Diego, CA"];
  const [selectedLocation, setSelectedLocation] = useState(locations[0]);

  const { data, isLoading, refetch } = useEagleTracker({
    event: selectedFunc,
    year,
  });

  useEffect(() => {
    if (data) {
      setEagleData(data);
    }
  }, [data]);

  const onEagleClicked = (ibo) => {
    history.push("/eagle-tracker-info", { ibo, year, event: selectedFunc });
  };

  const onSave = (settings) => {
    if (settings) {
      const bonusLegs =
        settings.find((s) => s.title === "Bonus Legs")?.value ?? 3;
      const pmLegs = settings.find((s) => s.title === "P.M. Legs")?.value ?? 1;
      const functions =
        settings.find((s) => s.title === "Function")?.value ?? 1;
      setFilterSettings({ bonusLegs, pmLegs, functions });
    }
    setOpened(false);
  };

  const statSources = [
    { name: "myInfo", data: eagleData?.myInfo },
    { name: "myInfo.stats", data: eagleData?.myInfo?.stats },
    { name: "myInfo.eagle", data: eagleData?.myInfo?.eagle },
    { name: "myParameters", data: eagleData?.myParameters },
    { name: "myParameters.stats", data: eagleData?.myParameters?.stats },
    { name: "iboInfo", data: eagleData?.iboInfo },
    { name: "parameters", data: eagleData?.parameters },
    { name: "summary", data: eagleData?.summary },
    { name: "stats", data: eagleData?.stats },
    { name: "metrics", data: eagleData?.metrics },
    { name: "root", data: eagleData },
  ];

  const normalizeMetric = (candidate) => {
    if (candidate === undefined || candidate === null) return null;
    if (typeof candidate === "object") {
      if ("value" in candidate || "trend" in candidate) return candidate;
      return null;
    }
    return { value: candidate };
  };

  const getMetric = (keys = []) => {
    for (const source of statSources) {
      if (!source?.data) continue;
      for (const key of keys) {
        const candidate = source.data?.[key];
        const normalized = normalizeMetric(candidate);
        if (normalized) {
          return {
            metric: normalized,
            source: source.name,
            key,
          };
        }
      }
    }
    return { metric: {}, source: "not_found", key: "not_found" };
  };

  const findDeepMetric = ({ aliases = [], fragments = [] }) => {
    if (!eagleData || typeof eagleData !== "object") {
      return { metric: {}, source: "not_found", key: "not_found" };
    }

    const skipKeys = new Set(["doubleEagles", "eagles", "trackingEagles"]);
    const queue = [{ node: eagleData, path: "root" }];
    let safetyCounter = 0;

    while (queue.length && safetyCounter < 500) {
      safetyCounter += 1;
      const { node, path } = queue.shift();
      if (!node || typeof node !== "object" || Array.isArray(node)) continue;

      const keys = Object.keys(node);

      for (const alias of aliases) {
        if (!keys.includes(alias)) continue;
        const normalized = normalizeMetric(node[alias]);
        if (normalized) {
          return {
            metric: normalized,
            source: path,
            key: alias,
          };
        }
      }

      for (const key of keys) {
        const keyLower = key.toLowerCase();
        if (!fragments.some((fragment) => keyLower.includes(fragment)))
          continue;
        const normalized = normalizeMetric(node[key]);
        if (normalized) {
          return {
            metric: normalized,
            source: path,
            key,
          };
        }
      }

      for (const key of keys) {
        if (skipKeys.has(key)) continue;
        const child = node[key];
        if (child && typeof child === "object" && !Array.isArray(child)) {
          queue.push({ node: child, path: `${path}.${key}` });
        }
      }
    }

    return { metric: {}, source: "not_found", key: "not_found" };
  };

  const pvResult = getMetric(STAT_KEY_MAP.pv.aliases);
  const vcsResult = getMetric(STAT_KEY_MAP.vcs.aliases);
  const legsResult = getMetric(STAT_KEY_MAP.legs.aliases);
  const qualificationResult = getMetric(STAT_KEY_MAP.qualification.aliases);

  const resolvedPvResult =
    pvResult.source === "not_found"
      ? findDeepMetric({
          aliases: STAT_KEY_MAP.pv.aliases,
          fragments: STAT_KEY_MAP.pv.fragments,
        })
      : pvResult;

  const resolvedVcsResult =
    vcsResult.source === "not_found"
      ? findDeepMetric({
          aliases: STAT_KEY_MAP.vcs.aliases,
          fragments: STAT_KEY_MAP.vcs.fragments,
        })
      : vcsResult;

  const resolvedLegsResult =
    legsResult.source === "not_found"
      ? findDeepMetric({
          aliases: STAT_KEY_MAP.legs.aliases,
          fragments: STAT_KEY_MAP.legs.fragments,
        })
      : legsResult;

  const resolvedQualificationResult =
    qualificationResult.source === "not_found"
      ? findDeepMetric({
          aliases: STAT_KEY_MAP.qualification.aliases,
          fragments: STAT_KEY_MAP.qualification.fragments,
        })
      : qualificationResult;

  const pvMetric = resolvedPvResult.metric;
  const vcsMetric = resolvedVcsResult.metric;
  const legsMetric = resolvedLegsResult.metric;
  const qualificationMetric = resolvedQualificationResult.metric;

  const getTrendColor = (metric, isVcs = false) => {
    const rawValue = metric?.value;
    const numericValue = Number(String(rawValue ?? "").replace("%", ""));

    if (isVcs && numericValue === 60) return "#FF9A00";
    if (metric?.trend === "down") return "#ED1F24";
    if (metric?.trend === "same") return "#FF9A00";
    return "#43BA32";
  };

  const vcsRawValue = vcsMetric?.value;
  const vcsNumericValue = Number(String(vcsRawValue ?? "").replace("%", ""));
  const vcsDisplayValue =
    vcsRawValue == null
      ? "—"
      : String(vcsRawValue).includes("%")
      ? String(vcsRawValue)
      : `${vcsRawValue}%`;
  const qualificationDisplayValue =
    qualificationMetric?.value === true
      ? t("Yes")
      : qualificationMetric?.value === false
      ? t("No")
      : qualificationMetric?.value ?? "—";
  const vcsColor =
    vcsNumericValue === 60
      ? "#FF9A00"
      : vcsMetric?.trend === "down"
      ? "#ED1F24"
      : vcsMetric?.trend === "same"
      ? "#FF9A00"
      : "#43BA32";

  useEffect(() => {
    if (!eagleData) return;

    // console.groupCollapsed("[EagleTracker] Stats mapping debug");
    // console.log("Top-level payload keys:", Object.keys(eagleData || {}));
    // console.table([
    //   {
    //     stat: "PV",
    //     source: resolvedPvResult.source,
    //     key: resolvedPvResult.key,
    //     value: pvMetric?.value,
    //     trend: pvMetric?.trend,
    //   },
    //   {
    //     stat: "VCS",
    //     source: resolvedVcsResult.source,
    //     key: resolvedVcsResult.key,
    //     value: vcsMetric?.value,
    //     trend: vcsMetric?.trend,
    //   },
    //   {
    //     stat: "Legs",
    //     source: resolvedLegsResult.source,
    //     key: resolvedLegsResult.key,
    //     value: legsMetric?.value,
    //     trend: legsMetric?.trend,
    //   },
    //   {
    //     stat: "Qualification",
    //     source: resolvedQualificationResult.source,
    //     key: resolvedQualificationResult.key,
    //     value: qualificationMetric?.value,
    //     trend: qualificationMetric?.trend,
    //   },
    // ]);
    // console.log("Raw eagleData payload:", eagleData);
    // console.groupEnd();
  }, [
    eagleData,
    resolvedPvResult.source,
    resolvedPvResult.key,
    pvMetric?.value,
    pvMetric?.trend,
    resolvedVcsResult.source,
    resolvedVcsResult.key,
    vcsMetric?.value,
    vcsMetric?.trend,
    resolvedLegsResult.source,
    resolvedLegsResult.key,
    legsMetric?.value,
    legsMetric?.trend,
    resolvedQualificationResult.source,
    resolvedQualificationResult.key,
    qualificationMetric?.value,
    qualificationMetric?.trend,
  ]);

  if (isLoading || !eagleData) {
    return (
      <div className={styles.loaderContainer}>
        <Loader color="#22252e" />
      </div>
    );
  }

  return (
    <div className={qualOpened ? styles.qualContainer : styles.container}>
      <SearchModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        items={[
          ...transformEagleList(eagleData.doubleEagles, onEagleClicked),
          ...transformEagleList(eagleData.eagles, onEagleClicked),
          ...transformEagleList(eagleData.trackingEagles, onEagleClicked),
        ]}
      />

      {!qualOpened && (
        <>
          <SettingsModal
            isOpen={opened}
            onCancel={() => setOpened(false)}
            onSave={onSave}
          />

          <QualifyHeader onClick={() => setQualOpened(true)} />
          <div className={styles.divider} />

          <div className={styles.selectHeader}>
            {t("Next Function to Qualify For:")}
          </div>
          <div className={styles.selectContainer}>
            <div className={styles.year}>
              <SelectDropdown
                options={years}
                onChange={setYear}
                placeholder={String(year)}
                disabled={false}
                isScrollable={true}
                visibleOptionsCount={3}
              />
            </div>
            <div className={styles.month}>
              <SelectDropdown
                options={months}
                onChange={setSelectedMonth}
                placeholder={selectedMonth}
                disabled={false}
                isScrollable={true}
                visibleOptionsCount={12}
              />
            </div>
            <div className={styles.search}>
              <img src={Search} onClick={() => setIsOpen(true)} />
            </div>
          </div>
          <div className={styles.selectContainer}>
            <div className={styles.func}>
              <SelectDropdown
                options={functions}
                onChange={setSelectedFunc}
                placeholder={t(selectedFunc)}
                disabled={false}
                isScrollable={true}
              />
            </div>
            <div className={styles.searchPlaceholder} />
          </div>

          <div className={styles.tabsWrapper}>
            <TagMonster
              categories={[
                { title: "My Parameters", rowNumber: 1 },
                { title: "Downline Eagles", rowNumber: 1 },
              ]}
              onClick={setActiveTab}
              selectedCategory={activeTab}
              variant="segmented"
              segmentedTheme="white"
            />
            {activeTab === "My Parameters" && (
              <div className={styles.statsRow}>
                <div className={styles.statCol}>
                  <span className={styles.statLabel}>{t("PV")}</span>
                  <span
                    className={styles.statValue}
                    style={{
                      color: getTrendColor(pvMetric),
                    }}
                  >
                    {pvMetric?.value ?? "—"}
                  </span>
                </div>
                <div className={styles.statCol}>
                  <span className={styles.statLabel}>{t("VCS")}</span>
                  <span
                    className={styles.statValue}
                    style={{ color: vcsColor }}
                  >
                    {vcsDisplayValue}
                  </span>
                </div>
                <div className={styles.statCol}>
                  <span className={styles.statLabel}>{t("Legs")}</span>
                  <span
                    className={styles.statValue}
                    style={{
                      color: getTrendColor(legsMetric),
                    }}
                  >
                    {legsMetric?.value ?? "—"}
                  </span>
                </div>
                <div className={styles.statCol}>
                  <span className={styles.statLabel}>{t("Qualification")}</span>
                  <span
                    className={styles.statValue}
                    style={{
                      color: getTrendColor(qualificationMetric),
                    }}
                  >
                    {qualificationDisplayValue}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* <div className={styles.location}>
            <SelectDropdown
              options={locations}
              onChange={setSelectedLocation}
              placeholder={selectedLocation}
              disabled={false}
              isScrollable={true}
            />
          </div> */}

          {activeTab === "My Parameters" &&
            (() => {
              const allEagles = [
                ...transformEagleList(eagleData.doubleEagles, onEagleClicked),
                ...transformEagleList(eagleData.eagles, onEagleClicked),
                ...transformEagleList(eagleData.trackingEagles, onEagleClicked),
              ];
              return (
                <>
                  <div className={styles.divider2} />
                  <EagleList
                    list={allEagles}
                    pvOnly={true}
                    headerDividerClassName={styles.headerDividerInList}
                    header={{
                      header: `Qualifying Legs (${allEagles.length})`,
                      color: "#91b6bb",
                      rightLabel: "PV",
                    }}
                  />
                </>
              );
            })()}

          {activeTab === "Downline Eagles" && (
            <>
              <div className={styles.divider2} />

              <EagleList
                list={transformEagleList(
                  eagleData.doubleEagles,
                  onEagleClicked
                )}
                header={{
                  header: t("Qualified Double Eagles"),
                  color: "#91b6bb",
                }}
              />
              <div className={styles.divider} />

              <EagleList
                list={transformEagleList(eagleData.eagles, onEagleClicked)}
                header={{ header: t("Qualified Eagles"), color: "#9e703c" }}
              />
              <div className={styles.divider} />

              <EagleList
                list={transformEagleList(
                  eagleData.trackingEagles.filter(
                    (e) =>
                      (e.totalBonus?.value ?? 0) >= filterSettings.bonusLegs
                  ),
                  onEagleClicked
                )}
                header={{
                  header: t("Tracking Eagles"),
                  color: "#adb7c0",
                  onClick: () => setOpened(true),
                  chip: `${filterSettings.bonusLegs}+ Legs`,
                }}
              />
            </>
          )}
        </>
      )}

      {qualOpened && (
        <div className={styles.qual}>
          <div className={styles.close} onClick={() => setQualOpened(false)}>
            <img src={Close} />
          </div>
          <div className={styles.qualImageWrapper}>
            <img className={styles.qualsvg} src={Qualifications} />
          </div>
        </div>
      )}
    </div>
  );
};
